import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import MediumEditor from 'medium-editor';
import AutoList from 'medium-editor-autolist';
import AutoHR from 'medium-editor-autohr';
import MediumEditorTable from 'medium-editor-tables';
import postal from 'postal';

import ErrorBoundary from '../../../../shared/error-boundary';
import Loading from '../../../../shared/loading';
import defaultOnErrorHandler from '../../../../shared/default-on-error-handler';
import debounceAction from '../../../../shared/debounce-action';
import {
  updateBody,
  deleteImageById,
  deleteImageByBlobSignedId,
} from '../../../../services/wire/edit-services';
import Validator from '../../../../shared/validator';
import { editorValidate } from '../channel-editor-postal';
import BlockquoteExtension from './medium-editor-extensions/blockquote-extension';
import TagExtension from './medium-editor-extensions/tag-extension';
import DragDropExtension from './medium-editor-extensions/dragdrop-extension';
import BackspaceWorkaroundExtension from './medium-editor-extensions/backspace-workaround';
import VideoHandler from './medium-editor-extensions/video-handler';
import DeleteEmbedCard from './medium-editor-extensions/delete-embed-card';
import MentionControl from './mention-control';
import { WarningIcon } from '../warning-icon';

require('jquery');
require('medium-editor-insert-plugin')($);

const editorId = 'wire-editor-body';

export default class Body extends React.Component {
  static propTypes = {
    wireId: PropTypes.number.isRequired,
    value: PropTypes.string.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.validator = new Validator({
      bodyRequired: () => {
        const bodyValue = this.editor && this.editor.serialize()[editorId].value;
        const $body = $(bodyValue);
        return bodyValue && ($body.text().trim().length || $body.find('img').length);
      },
    });

    this.state = {
      body: this.props.value || '',
      initialBody: this.props.value || '',
      showMentionables: false,
      validations: this.validator.getValidations(),
    };

    this.initMentionControlState();

    // Track in-flight inline image uploads so body autosave cannot race cleanup.
    this.inFlightImageUploads = 0;
    this.pendingBodySaveAfterUploads = false;

    [
      'handleInputChange',
      'handleNativeKeydownDelete',
      'handleMentionControlValueSelected',
      'handleMentionControlOnBlur',
      'handleMentionControlOnKeydown',
      'handleMentionControlOnBackspace',
      'handleShowMentionables',
      'serializeAndSaveBody',
      'deleteImage',
      'trackImageUploadStart',
      'trackImageUploadEnd',
    ].forEach(m => (this[m] = this[m].bind(this)));

    this.saveBody = debounceAction({
      getCurrentValue: () => this.state.body,
      getIsValid: () => {
        if (this.inFlightImageUploads > 0) {
          this.pendingBodySaveAfterUploads = true;
          return false;
        }
        return true;
      },
      actionPromise: () => updateBody(this.props.wireId, this.state.body),
      onBeforeAction: () => this.loading.start(),
      onError: e => defaultOnErrorHandler(e, 'Error saving body'),
      onAfterAction: () => {
        this.loading.stop();
        postal.publish({
          channel: 'editor',
          topic: 'wire-body-saved',
        });
      },
      wait: 2000,
    });

    postal.subscribe({
      channel: 'editor',
      topic: 'mention-control-value-selected',
      callback: this.handleMentionControlValueSelected,
    });

    postal.subscribe({
      channel: 'editor',
      topic: 'mention-control-on-blur',
      callback: this.handleMentionControlOnBlur,
    });

    postal.subscribe({
      channel: 'editor',
      topic: 'mention-control-on-keydown',
      callback: this.handleMentionControlOnKeydown,
    });

    postal.subscribe({
      channel: 'editor',
      topic: 'mention-control-on-backspace',
      callback: this.handleMentionControlOnBackspace,
    });

    editorValidate(this, 'body');
  }

  componentDidMount() {
    const buttons = [
      {
        name: 'bold',
        contentFA: '<span class="medium-editor-toolbar-button">B</span>',
      },
      {
        name: 'italic',
        contentFA: '<span class="medium-editor-toolbar-button"><italic>I</italic></span>',
      },
      'anchor',
      'tag-extension',
      {
        name: 'h2',
        contentFA: '<span class="medium-editor-toolbar-button">H<sup>2</sup></span>',
      },
      {
        name: 'h3',
        contentFA: '<span class="medium-editor-toolbar-button">H<sup>3</sup></span>',
      },
      'blockquote-extension',
      'orderedlist',
      'unorderedlist',
    ];

    const cleanTags = ['meta', 'img'];

    if (window.Livewire.features.editorTable) {
      buttons.push('table');
    } else {
      cleanTags.push('table');
    }

    const extensions = {
      autolist: new AutoList(),
      'blockquote-extension': new BlockquoteExtension(),
      'tag-extension': new TagExtension(),
      autohr: new AutoHR(),
      'backspace-workaround': new BackspaceWorkaroundExtension(),
      'video-handler': new VideoHandler(),
      imageDragging: {},
      'delete-embed-card': new DeleteEmbedCard(),
      table: new MediumEditorTable(),
      'dragdrop-extension': new DragDropExtension(),
    };

    this.editor = new MediumEditor(`#${editorId}`, {
      buttonLabels: 'fontawesome',
      imageDragging: false,
      paste: {
        forcePlainText: false,
        cleanPastedHTML: true,
        cleanTags,
        unwrapTags: ['h1', 'h4', 'h5', 'h6'],
        cleanAttrs: [
          'class', 'style', 'dir', 'width', 'height', 'id', 'rel',
          'data-controller',
          'data-tooltip-url-value',
          'data-event-name-value',
          'data-event-event-value',
          'data-event-statisticable-value',
          'data-event-id-value', 'data-event-type',
          'data-zoom-target', 'data-target-type', 'data-target-id',
          'data-media-player-service-type-value', 'data-player-id',
          'data-url', 'data-link-url-value', 'data-hotwired',
          'data-type', 'data-wire-id', 'data-video-id', 'data-user-signed-in',
        ],
      },
      toolbar: {
        buttons,
      },
      placeholder: {
        text: 'Create your wire here',
      },
      extensions,
    });

    this.editor.subscribe('showMentionables', this.handleShowMentionables);
    this.editor.subscribe('editableInput', this.handleInputChange);
    this.editor.subscribe('editableKeydownDelete', this.handleNativeKeydownDelete);

    const $mediumInsert = $(`#${editorId}`).mediumInsert({
      editor: this.editor,
      enabled: true,
      addons: {
        images: {
          deleteScript: (file, $el) => this.deleteImage(file || $el.attr('src')),
          fileUploadOptions: {
            url: `/api/v1/wire/${this.props.wireId}/image`,
            acceptFileTypes: /(\.|\/)(gif|jpe?g|png)$/i,
            // send/always survive mediumInsert's own add/done overrides; used by
            // toolbar upload and drag-drop (which merges these options).
            send: this.trackImageUploadStart,
            always: this.trackImageUploadEnd,
          },
          captions: true,
          captionPlaceholder: 'Enter your image caption...',
          uploadCompleted: $el => {
            $el.find('img').click();
            setTimeout(() => {
              this.setupImageCaption($el, true);
            }, 50);
          },
          actions: null,
          preview: false,
        },
        embeds: false,
      },
    });

    window.mediumInsertImages = $mediumInsert.data('plugin_mediumInsertImages');
    window.mediumInsert = $mediumInsert.data('plugin_mediumInsert');
    window.mediumInsert.removeCaptions = () => {};
    window.mediumInsert.removeCaptionPlaceholder = () => {};

    $mediumInsert.find('.medium-insert-images > figure').each((index, figure) => {
      this.setupImageCaption($(figure));
    });

    this.removeUnnecessaryElements();
    this.detectCannotDeleteLines();
    this.addEmptyLineBetweenSpecialTagsAndEOW();
    this.addEmptyLineBeforeFirstBlockElement();
  }

  /* eslint-disable react/sort-comp */
  // because we have method names tricking eslinter to think it's a setter method
  trackImageUploadStart() {
    this.inFlightImageUploads += 1;
  }

  trackImageUploadEnd() {
    this.inFlightImageUploads = Math.max(0, this.inFlightImageUploads - 1);
    if (this.inFlightImageUploads === 0 && this.pendingBodySaveAfterUploads) {
      this.pendingBodySaveAfterUploads = false;
      this.serializeAndSaveBody();
    }
  }

  deleteImage(url) {
    const imageId = _.get(url.match(/\/uploads\/media\/(\d+)\/.*/), '[1]');
    if (imageId) {
      deleteImageById(this.props.wireId, imageId);
    } else {
      this.deleteImageBlob(url);
    }
  }

  // blob URL pattern: <protocol>://<host>/rails/active_storage/blobs/redirect/<signedId>/<filename>.<extension>
  deleteImageBlob(url) {
    const regex = /\/rails\/active_storage\/.+(\/(proxy|redirect)?)\/(.+)\/.+/;
    const match = regex.exec(url);
    if (_.get(match, 'length') >= 2) {
      deleteImageByBlobSignedId(this.props.wireId, match[match.length - 1]);
    }
  }

  setupImageCaption = (figure, uploaded) => {
    const info = '<div class="no-caption" contenteditable="false">'
      + '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">'
      + '<path d="M7.99998 5.33317V7.99984M7.99998 10.6665H8.00665M14.6666 7.99984C14.6666 4.31794 11.6819 1.33317 7.99998 1.33317C4.31808 1.33317 1.33331 4.31794 1.33331 7.99984C1.33331 11.6817 4.31808 14.6665 7.99998 14.6665C11.6819 14.6665 14.6666 11.6817 14.6666 7.99984Z" stroke="#B71E49" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg></div>';
    const { captionPlaceholder } = window.mediumInsert.options.addons.images;
    let figcaption = figure.find('figcaption').get(0);

    if (!figcaption) {
      figure.append('<figcaption contenteditable="true"></figcaption>');
      figcaption = figure.find('figcaption').get(0);
    }

    figure.find('.no-caption').remove();

    if (figcaption.textContent.trim().length === 0) {
      figure.append(info);
      figcaption.classList.add('medium-insert-caption-placeholder');
      figcaption.setAttribute('data-placeholder', captionPlaceholder);
    }

    const container = figure.parent().get(0);
    const hideImageUploadIcon = () => setTimeout(() => {
      $('.medium-insert-buttons').remove();
    }, 100);

    container.addEventListener('click', hideImageUploadIcon);
    container.addEventListener('focus', hideImageUploadIcon);

    figcaption.addEventListener('click', hideImageUploadIcon);
    figcaption.addEventListener('focus', hideImageUploadIcon);

    figcaption.addEventListener('keydown', e => {
      if (e.key === 'Backspace') {
        e.stopPropagation();

        if (figcaption.children.length > 0 && figcaption.textContent.length === 0) {
          figcaption.innerHTML = '';
        } else if (figcaption.textContent.length === 0) {
          figure.get(0).parentNode.remove();
          this.serializeAndSaveBody();
        }
      }
    });

    figcaption.addEventListener('keyup', e => {
      figure.find('.no-caption').remove();

      const hasCaption = figcaption.textContent.trim().length > 0;
      if (hasCaption) {
        figcaption.removeAttribute('data-placeholder');
        figcaption.classList.remove('medium-insert-caption-placeholder');
      } else {
        figcaption.setAttribute('data-placeholder', captionPlaceholder);
        figcaption.classList.add('medium-insert-caption-placeholder');
        figure.append(info);
      }

      const image = figure.find('img');
      image.attr('alt', e.currentTarget.textContent);

      hideImageUploadIcon();
    });

    const mentionables = document.getElementById('mentionables');

    mentionables.addEventListener('keyup', () => {
      figure.find('.no-caption').remove();
      const hasText = mentionables.textContent.trim().length > 0;
      if (hasText) {
        figcaption.classList.remove('medium-insert-caption-placeholder');
      } else {
        figcaption.classList.add('medium-insert-caption-placeholder');
      }
    });

    if (uploaded === true) {
      $('.medium-insert-images-toolbar').hide();
      figcaption.click();
      MediumEditor.selection.moveCursor(document, figcaption);
    }
  }

  initMentionControlState() {
    this.mentionControlState = {
      mentionableSelected: false,
      backspacePressed: false,
    };
  }

  wrapTables() {
    const body = document.querySelector('section.wire-body.full-wire');
    body.querySelectorAll('table').forEach(table => {
      const hasWrapper = table.parentNode.tagName === 'DIV'
        && table.parentNode.classList.contains('medium-editor-table-wrapper');
      if (!hasWrapper) {
        this.wrapTable(table);
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  wrapTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('medium-editor-table-wrapper');
    table.insertAdjacentElement('beforebegin', wrapper);
    wrapper.insertAdjacentElement('afterbegin', table);
    table.classList.add('medium-editor-table');

    table.setAttribute('contenteditable', 'true');
    wrapper.setAttribute('contenteditable', 'false');

    const newline = document.createElement('p');
    newline.innerHTML = '<br>';
    wrapper.insertAdjacentElement('afterend', newline);
    MediumEditor.selection.moveCursor(document, newline);
  }

  // eslint-disable-next-line class-methods-use-this
  removeUnnecessaryElements() {
    const body = document.querySelector('section.wire-body.full-wire');
    const tobeRemovedSelectors = [
      '.wire-editor-body > p:empty',
      '.wire-editor-body > h2:empty',
      '.wire-editor-body > h3:empty',
      '.wire-editor-body > div:empty',
      'div.medium-editor-table-wrapper > :not(table)',
    ];
    body.querySelectorAll(tobeRemovedSelectors.join(', ')).forEach(element => element.remove());
  }

  // eslint-disable-next-line class-methods-use-this
  detectCannotDeleteLines() {
    const body = document.querySelector('section.wire-body.full-wire');

    body.querySelectorAll('.medium-insert-images, .medium-editor-table-wrapper').forEach(element => {
      const next = element.nextElementSibling;
      if (next) {
        const isAnImageOrTable = next.classList.contains('medium-insert-images') || next.classList.contains('medium-editor-table-wrapper');
        const isBlank = next.innerHTML.trim() === '' || next.innerHTML.trim() === '<br>';

        if (isAnImageOrTable) {
          element.insertAdjacentHTML('afterend', '<p><br></p>');
        } else if (isBlank && next.nextElementSibling) {
          const isNotNormalParagraph = ['H2', 'H3', 'DIV', 'BLOCKQUOTE'].indexOf(next.nextElementSibling.tagName) > -1;
          if (isNotNormalParagraph) {
            next.remove();
          }
        }
      }
    });

    const deletableSelectors = [
      '.wire-editor-body > h2',
      '.wire-editor-body > h3',
      '.wire-editor-body > div',
      '.wire-editor-body > blockquote',
      '.wire-editor-body > p',
    ];
    body.querySelectorAll(deletableSelectors.join(', ')).forEach(line => {
      const previous = line.previousElementSibling;

      const deletable = previous
        && (previous.classList.contains('medium-insert-images')
          || previous.classList.contains('medium-editor-table-wrapper'));

      if (deletable) {
        line.classList.add('can-not-delete');
      } else {
        line.classList.remove('can-not-delete');
      }
    });
  }

  // eslint-disable-next-line class-methods-use-this
  addEmptyLineBetweenSpecialTagsAndEOW() {
    const body = document.querySelector('section.wire-body.full-wire');
    const querySelectors = [
      'div.embedded-video.youtube-player-v2',
      'table',
    ].join(', ');
    body.querySelectorAll(querySelectors).forEach(element => {
      if (element.nextElementSibling?.classList?.contains('medium-insert-buttons')) {
        element.insertAdjacentHTML('afterend', '<p><br></p>');
      }
    });
  }

  // Add empty line before block elements (youtube, soundcloud, podbean)
  // This allows users to position cursor before the block to select/delete it
  // eslint-disable-next-line class-methods-use-this
  addEmptyLineBeforeFirstBlockElement() {
    const editorBody = document.getElementById(editorId);
    if (!editorBody) return;

    const blockSelectors = [
      'div.youtube-player-v2',
      'iframe[src*="soundcloud"]',
      'iframe[src*="podbean"]',
    ].join(', ');

    editorBody.querySelectorAll(blockSelectors).forEach(element => {
      const prev = element.previousElementSibling;
      // Only add if no previous sibling, or previous is empty <p> without <br>
      const hasPrevWithBr = prev && prev.tagName === 'P' && prev.querySelector('br');
      if (hasPrevWithBr) return;

      if (!prev || (prev.tagName === 'P' && !prev.textContent.trim())) {
        element.insertAdjacentHTML('beforebegin', '<p><br></p>');
      }
    });
  }

  serializeAndSaveBody() {
    this.wrapTables();
    this.removeUnnecessaryElements();
    this.detectCannotDeleteLines();
    this.setState(state => {
      state.body = this.editor.serialize()[editorId].value;
      state.validations = this.validator.validateForm(state);
      return state;
    }, this.saveBody);
  }

  handleInputChange(event) {
    if (event && event.data === '@' && this.isTheFirstCharacterOrPrecededByWhitespaceOrBracket()) {
      this.keepStatusOfTheCurrentEditingElement();
      this.initMentionControlState();
      this.setupNewMentionElement();
      this.showTheMentionables();
    } else if (!this.state.showMentionables) {
      this.serializeAndSaveBody();
    }
  }

  handleNativeKeydownDelete(event) {
    const currentLine = this.editor.getSelectedParentElement();
    const parent = currentLine.parentNode;
    const previousSibling = currentLine.previousElementSibling;
    if (currentLine.tagName === 'FIGURE'
      && parent.classList.contains('medium-insert-images')) {
      parent.nextElementSibling.classList.remove('can-not-delete');
      parent.remove();
      event.preventDefault();
      event.stopPropagation();
      this.serializeAndSaveBody();
    } else if (currentLine.classList.contains('can-not-delete')
      && currentLine.innerHTML === '<br>') {
      event.preventDefault();
      event.stopPropagation();
      if (previousSibling.classList.contains('medium-insert-images')) {
        currentLine.remove();
        this.serializeAndSaveBody();
      }
    } else {
      const caret = MediumEditor.selection.getCaretOffsets(currentLine).left;
      if (caret === 0 && previousSibling && (previousSibling.classList.contains('embedded-video') || previousSibling.getElementsByClassName('embedded-video').length > 0)) {
        previousSibling.remove();
        event.preventDefault();
        event.stopPropagation();
        this.serializeAndSaveBody();
      }
    }
  }

  isTheFirstCharacterOrPrecededByWhitespaceOrBracket() {
    const element = this.editor.getSelectedParentElement();
    const caret = MediumEditor.selection.getCaretOffsets(element).left;

    if (caret === 1) {
      return true;
    }

    const previousChar = $(element).text().charAt(caret - 2);

    return previousChar.trim().length === 0 || previousChar === '(';
  }

  keepStatusOfTheCurrentEditingElement() {
    const element = this.editor.getSelectedParentElement();
    this.currentCaretPosition = MediumEditor.selection.getCaretOffsets(element).left;
    this.currentEditingElement = $(element);
  }

  setupNewMentionElement() {
    const children = this.currentEditingElement.contents().toArray();
    let textLength = 0;

    this.newMentionElement = $('<span>@</span>');
    this.newSpaceElement = $('<span>&nbsp;</span>');
    this.currentEditingElement.empty();

    _(children).each(child => {
      const content = child.textContent;
      const len = content.length;

      if (textLength < this.currentCaretPosition && textLength + len >= this.currentCaretPosition) {
        const before = content.substring(0, this.currentCaretPosition - textLength - 1);
        const after = content.substring(this.currentCaretPosition - textLength, len);

        this.currentEditingElement
          .append(before)
          .append(this.newMentionElement)
          .append(this.newSpaceElement)
          .append(after);
      } else {
        this.currentEditingElement.append(child);
      }

      textLength += len;
    });

    const selection = this.editor.selectElement(this.newMentionElement.get(0));
    if (selection) {
      MediumEditor.selection.clearSelection(document);
    }
  }

  handleShowMentionables() {
    const newPosition = window.getSelection().getRangeAt(0).getBoundingClientRect();
    this.highlightedText = window.getSelection().toString();
    this.currentEditingElement = $(this.editor.getSelectedParentElement());
    this.showTheMentionables(newPosition);
  }

  showTheMentionables(newPosition) {
    if (newPosition) {
      $('#mentionables').css({
        top: newPosition.top - 22,
        left: newPosition.left,
        position: 'fixed',
        display: 'block',
      });

      document.querySelector('#mentionables').classList.add('highlighted-text');
    } else {
      const standfirstHeight = document.querySelector('.wire-standfirst-summary textarea').offsetHeight;
      const headerHeight = document.querySelector('.wire-editor header').offsetHeight;
      const warningHeight = document.querySelector('.impersona-warning-prompt')?.offsetHeight || 0;
      const isForFigcaption = this.currentEditingElement.prop('tagName') === 'FIGCAPTION' || this.currentEditingElement.parents('figcaption').length;
      const totalHeight = standfirstHeight + headerHeight + warningHeight;
      const newMentionElementPosition = this.newMentionElement.position();
      const newMentionElementOffset = this.newMentionElement.offset();
      const figcaptionTop = newMentionElementOffset.top - 222.5 - totalHeight;

      $('#mentionables').css({
        top: isForFigcaption ? figcaptionTop : newMentionElementPosition.top - 22,
        left: newMentionElementPosition.left + 18,
        position: 'absolute',
        display: 'block',
      });
    }

    this.setState({ showMentionables: true });
    this.mentionablesRef.setFocus();

    if (this.highlightedText) {
      this.mentionablesRef.onInputChange(this.highlightedText);
    }
  }

  handleMentionControlValueSelected(mentionable) {
    const nameToShow = mentionable.isCompanyProfile && mentionable.company
      ? mentionable.company : mentionable.name;
    this.mentionControlState.mentionableSelected = true;

    const mentionableHtml = `<a href="${mentionable.publicUrl}" target="_blank" data-tip="true" data-for="${mentionable.type}-${mentionable.id}">${nameToShow}</a>`;

    if (this.highlightedText) {
      const currentLine = this.currentEditingElement.get(0);
      currentLine.innerHTML = currentLine.innerHTML.replace(
        this.highlightedText, mentionableHtml,
      );
      this.escapeMentionControl();
    } else {
      this.newMentionElement.replaceWith($(mentionableHtml));
      this.insertNewMentionElement();
    }
  }

  handleMentionControlOnBlur() {
    const { mentionableSelected, backspacePressed } = this.mentionControlState;
    if (mentionableSelected || backspacePressed) {
      return;
    }

    this.escapeMentionControl();
  }

  handleMentionControlOnKeydown(event) {
    const { key } = event;

    if (key === 'Escape') {
      this.escapeMentionControl();
    }
  }

  handleMentionControlOnBackspace() {
    this.mentionControlState.backspacePressed = true;
    this.setState({ showMentionables: false });
    this.newSpaceElement.remove();
    this.newMentionElement.empty();
    const parent = this.newMentionElement.get(0).parentNode;

    if (parent.classList.contains('medium-insert-active') || parent.textContent.trim().length === 0) {
      parent.innerHTML = '<br>';
      MediumEditor.selection.moveCursor(document, parent);
    } else {
      MediumEditor.selection.moveCursor(document, this.newMentionElement.get(0));
      this.newMentionElement.remove();
    }

    this.initMentionControlState();
    this.serializeAndSaveBody();
  }

  insertNewMentionElement() {
    this.setState({ showMentionables: false });
    MediumEditor.selection.moveCursor(document, this.newSpaceElement.get(0), 1);
    this.serializeAndSaveBody();
  }

  escapeMentionControl() {
    this.initMentionControlState();
    this.setState({ showMentionables: false });
    this.removeHighlightedText();
    if (this.newMentionElement && this.newSpaceElement) {
      this.newSpaceElement.remove();
      MediumEditor.selection.moveCursor(document, this.newMentionElement.get(0), 1);
    }
    this.serializeAndSaveBody();
  }

  removeHighlightedText() {
    if (this.highlightedText) {
      delete this.highlightedText;
      this.mentionablesRef.onInputChange('');
      document.querySelector('#mentionables').classList.remove('highlighted-text');
    }
  }
  /* eslint-enable react/sort-comp */

  render() {
    return (
      <ErrorBoundary>
        <section className={`wire-body full-wire ${this.state.validations.formValid ? '' : 'has-error'}`}>
          {!!this.state.validations.bodyRequiredValid || <span className="help-block">Please ensure your wire content is not empty.</span>}
          <div
            className="wire-editor-body"
            id={editorId}
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: this.state.initialBody,
            }}
          />
          <input name="wire[body]" type="hidden" value={this.state.body} />
          <p className="sip-controls">
            <Loading variant="sip" ref={el => (this.loading = el)} />
          </p>
          <div id="mentionables" style={{ display: this.state.showMentionables ? 'block' : 'none' }}>
            <MentionControl
              menuIsOpen
              ref={el => (this.mentionablesRef = el)}
            />
          </div>
        </section>
        {
          this.state.body.length >= 40_000_000 && (
            <div className="flex justify-end mt-2 text-sm text-right font-semibold">
              <div className="warning flex">
                <WarningIcon />
                <span>Body is too large (maximum size is 4MB)</span>
              </div>
            </div>
          )
        }
      </ErrorBoundary>
    );
  }
}
